/** WOGAA Props
 * */
export interface WOGAATransactionProps {
  transactionStart: () => void;
  transactionEnd: (agencyTxnId?: string) => void;
}

/**
 * Key used to persist the currently-active WOGAA tracking ID across
 * component remounts / route changes / page refreshes.
 */
const ACTIVE_WOGAA_KEY = 'wogaa:activeTrackingID';

/** Read the currently-active tracking ID (if any). */
function getActiveTrackingID(): string | null {
  try {
    return sessionStorage.getItem(ACTIVE_WOGAA_KEY);
  } catch {
    return null;
  }
}

/** Persist the currently-active tracking ID. */
function setActiveTrackingID(id: string | null): void {
  try {
    if (id) {
      sessionStorage.setItem(ACTIVE_WOGAA_KEY, id);
    } else {
      sessionStorage.removeItem(ACTIVE_WOGAA_KEY);
    }
  } catch {
    /* ignore storage errors (e.g. private mode) */
  }
}

/** Tell WOGAA to complete a transaction (safe wrapper). */
function completeWOGAA(id: string): void {
  try {
    // Small delay to ensure the WOGAA sentiment popup renders
    // after the page has fully settled (prevents flash and disappear).
    setTimeout(() => {
      // @ts-ignore
      window.wogaaCustom?.completeTransactionalService(id);
    }, 1000);
  } catch {
    /* no-op */
  }
}

/** Complete an abandoned transaction immediately (no delay).
 *  Must fire before startTransactionalService so WOGAA properly
 *  resets its TS lifecycle. The popup won't render because
 *  startTransactionalService is called right after.
 */
function completeAbandonedWOGAA(id: string): void {
  try {
    // @ts-ignore
    window.wogaaCustom?.completeTransactionalService(id);
  } catch {
    /* no-op */
  }
}

/** Tell WOGAA to add meta data before completing transaction (safe wrapper). */
function addMetaWOGAA(agencyTxnId: string): void {
  try {
    // @ts-ignore
    window.wogaaCustom?.addMeta({ agencyTxnId });
  } catch {
    /* no-op */
  }
}

/** Tell WOGAA to start a transaction (safe wrapper). */
function startWOGAA(id: string): void {
  try {
    // @ts-ignore
    window.wogaaCustom?.startTransactionalService(id);
  } catch {
    /* no-op */
  }
}

/** WOGAA Hook
 *
 * Ensures that only ONE WOGAA transaction is active at a time.
 * If a previous transaction (e.g. Registration) was never completed and the
 * user moves on to a different one (e.g. Account Activation), the old one
 * is auto-completed (treated as abandoned) before the new one starts.
 */
export function useWOGAA(trackingID: string): WOGAATransactionProps {

  /** start WOGAA transaction */
  const startWOGAATransaction = () => {
    const active = getActiveTrackingID();

    // If another transaction is still open, close it first (immediately,
    // before starting the new one, so WOGAA resets its TS lifecycle).
    if (active && active !== trackingID) {
      completeAbandonedWOGAA(active);
    }

    // Avoid starting the same transaction twice in a row.
    if (active === trackingID) {
      return;
    }

    startWOGAA(trackingID);
    setActiveTrackingID(trackingID);
  };

  /** end WOGAA transaction */
  const endWOGAATransaction = (agencyTxnId?: string) => {
    const active = getActiveTrackingID();

    // Only end if this hook's tracking ID is actually the active one.
    if (active && active === trackingID) {
      if (agencyTxnId) {
        addMetaWOGAA(agencyTxnId);
      }
      completeWOGAA(trackingID);
      setActiveTrackingID(null);
    }
  };

  return {
    transactionStart: startWOGAATransaction,
    transactionEnd: endWOGAATransaction,
  };
}

